const React = require('react')
const { useState } = require('react')
const { useHistory } = require('react-router-dom')
const { Table, UncontrolledTooltip } = require('reactstrap')
const { useFormatBytes, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { CopyButton } = require('@components')

require('@styles/pages/blocks/index.css')

const BlocksTableVersion2 = props => {
    const { data } = props

    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()
    const [showSlot, setShowSlot] = useState(true)
    const [showAge, setShowAge] = useState(true)
    const isMobile = useResponsive(BREAKPOINTS.medium)

    return (
        <Table responsive style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}>
            <thead>
                <tr>
                    <th>Block</th>
                    <th>
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => setShowSlot(!showSlot)}
                        >
                            {showSlot ? 'Slot' : 'Epoch'}
                        </span>
                    </th>
                    <th>
                        <span
                            id='age-tooltip'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => setShowAge(!showAge)}
                        >
                            {showAge ? 'Age' : 'Date Time (UTC)'}
                        </span>
                        <UncontrolledTooltip placement='top' target='age-tooltip'>
                            {showAge ? 'Click to show Datetime Format' : 'Click to show Age Format'}
                        </UncontrolledTooltip>
                    </th>
                    <th>Txn</th>
                    <th>Fee Recipient</th>
                    <th>Gas Used</th>
                    <th>Gas Limit</th>
                    <th>Base Fee</th>
                    <th>Reward</th>
                    <th>Burnt Fees (Gwei)</th>
                </tr>
            </thead>
            <tbody>
                {data?.map(block => (
                    <tr key={block.number}>
                        <td>
                            <span
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => history.push(`/${network.name}/block/${block.number}`)}
                            >
                                {block.number}
                            </span>
                        </td>
                        <td>
                            {showSlot ? (
                                <>
                                    <span
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => history.push(`/${network.name}/slot/${block.beacon.slot}`)}
                                    >
                                        {block.beacon.slot}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => history.push(`/${network.name}/epoch/${block.beacon.epoch}`)}
                                    >
                                        {block.beacon.epoch}
                                    </span>
                                </>
                            )}
                        </td>
                        <td>
                            {showAge ? (
                                <>
                                    <span id={'age-' + block.number}>{formatTimestamp(block.timestamp)}</span>
                                    <UncontrolledTooltip placement='top' target={`age-${block.number}`}>
                                        {formatDateTime(block.timestamp)}
                                    </UncontrolledTooltip>
                                </>
                            ) : (
                                <>
                                    <span id={'datetime-' + block.number}>{formatDateTime(block.timestamp)}</span>
                                    <UncontrolledTooltip placement='top' target={`datetime-${block.number}`}>
                                        {formatTimestamp(block.timestamp)}
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </td>
                        <td>
                            {block.transactionCount > 0 ? (
                                <span
                                    className={`${network.name}-text-color cursor-pointer`}
                                    onClick={() => history.push(`/${network.name}/txs?block=${block.number}`)}
                                >
                                    {block.transactionCount}
                                </span>
                            ) : (
                                block.transactionCount
                            )}
                        </td>
                        <td>
                            <span
                                id={`validator-${block.number}`}
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => history.push(`/${network.name}/address/${block.validator}`)}
                            >
                                {formatBytes(block.validator, 40, 10, 10)}
                            </span>
                            <UncontrolledTooltip placement='top' target={`validator-${block.number}`}>
                                {block.validator}
                            </UncontrolledTooltip>
                            &nbsp;
                            <CopyButton
                                id={`copy-validator-${block.number}`}
                                data={block.validator}
                                text={'Copy address'}
                            />
                        </td>
                        <td>
                            {block.gasUsed.toLocaleString()}&nbsp;
                            <span
                                id={`percent-gas-used-${block.number}`}
                                style={{ color: 'var(--grey-color)', fontSize: '12px' }}
                            >
                                ({((block.gasUsed / block.gasLimit) * 100).toFixed(2)}%)
                            </span>
                            <UncontrolledTooltip placement='top' target={`percent-gas-used-${block.number}`}>
                                Gas Used %
                            </UncontrolledTooltip>
                        </td>
                        <td>{block.gasLimit.toLocaleString()}</td>
                        <td>
                            {block.baseFeePerGas.toLocaleString()}
                            &nbsp;wei
                        </td>
                        <td>
                            {(
                                (block.totalTransactionFees - block.baseFeePerGas * block.gasUsed) /
                                10 ** 9
                            ).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            })}
                            &nbsp;Gwei
                        </td>
                        <td>
                            {((block.baseFeePerGas * block.gasUsed) / 10 ** 9).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 4
                            })}
                            &nbsp;
                            <span
                                id={`percent-gas-burnt-${block.number}`}
                                style={{ color: 'var(--grey-color)', fontSize: '12px' }}
                            >
                                (
                                {(
                                    ((block.baseFeePerGas * block.gasUsed) / block.totalTransactionFees) * 100 || 0
                                ).toFixed(2)}
                                %)
                            </span>
                            <UncontrolledTooltip placement='top' target={`percent-gas-burnt-${block.number}`}>
                                Gas Burnt %
                            </UncontrolledTooltip>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

module.exports = { BlocksTableVersion2 }
